import React, { useEffect, useRef, useState } from 'react';
import { useTenant } from 'src/contexts/TenantContext';

declare global {
  interface Window {
    google: any;
    gapi: any;
  }
}

interface GoogleDrivePickerProps {
  onFilesReceived?: (files: File[]) => void;
  onProcessingStart?: (fileCount: number) => void;
  children?: React.ReactNode;
}

export default function GoogleDrivePicker({
  onFilesReceived,
  onProcessingStart,
  children,
}: GoogleDrivePickerProps) {
  const tokenClientRef = useRef<any>(null);
  const [pickerInited, setPickerInited] = useState(false);
  const [gisInited, setGisInited] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const { getConfigValue } = useTenant();
  const apiAccessScopes = 'https://www.googleapis.com/auth/drive';
  const clientId = getConfigValue('MEDIA_PROVIDER_GOOGLE_DRIVE_CLIENT_ID');
  const apiKey = getConfigValue('MEDIA_PROVIDER_GOOGLE_DRIVE_API_KEY');
  const appId = getConfigValue('MEDIA_PROVIDER_GOOGLE_DRIVE_APP_ID');

  useEffect(() => {
    // Initialize APIs once they're loaded
    if (window.gapi) {
      gapiLoaded();
    }
    if (window.google) {
      gisLoaded();
    }
  }, []);

  const gapiLoaded = () => {
    window.gapi.load('client:picker', initializePicker);
  };

  const initializePicker = async () => {
    await window.gapi.client.load(
      'https://www.googleapis.com/discovery/v1/apis/drive/v3/rest',
    );
    setPickerInited(true);
  };

  const gisLoaded = () => {
    tokenClientRef.current = window.google.accounts.oauth2.initTokenClient({
      client_id: clientId,
      scope: apiAccessScopes,
      prompt: 'consent', // Always show consent screen
      callback: '', // Defined on demand
    });
    setGisInited(true);
  };

  const maybeEnableButtons = () => pickerInited && gisInited && !isLoading;

  const createPicker = (accessToken: string) => {
    const view = new window.google.picker.View(
      window.google.picker.ViewId.DOCS,
    );
    view.setMimeTypes('image/png,image/jpeg,image/jpg');

    const picker = new window.google.picker.PickerBuilder()
      .enableFeature(window.google.picker.Feature.NAV_HIDDEN)
      .enableFeature(window.google.picker.Feature.MULTISELECT_ENABLED)
      .setDeveloperKey(apiKey)
      .setAppId(appId)
      .setOAuthToken(accessToken)
      .addView(view)
      .addView(new window.google.picker.DocsUploadView())
      .setCallback((data: any) => pickerCallback(data, accessToken))
      .build();

    picker.setVisible(true);
  };

  const handleAuthClick = () => {
    if (!tokenClientRef.current) return;

    // Set up the callback to handle the auth response
    tokenClientRef.current.callback = (response: any) => {
      if (response.error) {
        console.error(response);
        return;
      }
      // Use the access token immediately without storing it
      createPicker(response.access_token);
    };

    // Always request a new token with consent screen
    tokenClientRef.current.requestAccessToken({ prompt: 'consent' });
  };

  // Convert the binary string from Google to a way it can be read
  function binaryStringToUint8Array(str: string) {
    const len = str.length;
    const bytes = new Uint8Array(len);
    for (let i = 0; i < len; i++) {
      bytes[i] = str.charCodeAt(i);
    }
    return bytes;
  }

  const pickerCallback = async (data: any, accessToken: string) => {
    if (data.action === window.google.picker.Action.PICKED) {
      const documents = data[window.google.picker.Response.DOCUMENTS];

      // Notify parent that processing is starting
      onProcessingStart?.(documents.length);

      setIsLoading(true);
      try {
        const files = await Promise.all(
          documents.map(async (doc: any) => {
            const fileId = doc[window.google.picker.Document.ID];
            const fileName =
              doc[window.google.picker.Document.NAME] || `file_${fileId}`;

            try {
              // Use the access token directly from the callback
              window.gapi.client.setToken({ access_token: accessToken });

              const res = await window.gapi.client.drive.files.get({
                isAppAuthorized: true,
                fileId,
                alt: 'media',
                fields: '*',
              });

              // The response comes in as binary, and needs to be converted to a blob
              const jpegBytes = binaryStringToUint8Array(res.body);
              const blob = new Blob([jpegBytes], { type: 'image/jpeg' });

              // Create File object directly (no intermediate blob URL!)
              const fileObject = new File([blob], fileName, {
                type: blob.type || 'image/jpeg',
              });

              return fileObject;
            } catch (err) {
              console.error(`Error fetching file ${fileId}:`, err);
              return null;
            }
          }),
        );

        // Filter out any failed files and call the callback
        const validFiles = files.filter((f): f is File => f !== null);
        if (validFiles.length > 0 && onFilesReceived) {
          onFilesReceived(validFiles);
        }
      } catch (err) {
        console.error('Error processing selected files:', err);
      } finally {
        setIsLoading(false);
      }
    }
  };

  return (
    <button
      onClick={handleAuthClick}
      disabled={!maybeEnableButtons()}
      style={{
        cursor: 'pointer',
        background: 'none',
        border: 'none',
        display: 'flex',
        alignItems: 'center',
        height: 64,
        gap: '12px',
        paddingInline: '20px',
        flexDirection: 'row',
        padding: 0,
        width: 150,
        justifyContent: 'center',
      }}
    >
      {children || 'Google Drive'}
    </button>
  );
}
